import * as React from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ToastProvider } from "../../../modules/superadmin/space-manager/components/ui/Toast";
import { SpaceLoader } from "../../../modules/superadmin/space-manager/components/space/SpaceLoader";
import { TextureMapping } from "../../../modules/superadmin/space-manager/components/textures/TextureMapping";
import { ProductSidebar } from "../../../modules/superadmin/space-manager/components/textures/ProductSidebar";
import { SurfaceManagement } from "../../../modules/superadmin/space-manager/components/surfaces/SurfaceManagement";
import { ShadingSection } from "../../../modules/superadmin/space-manager/components/shading/ShadingSection";
import { PlaneEditor3D } from "../../../modules/superadmin/space-manager/components/geometry/PlaneEditor3D";
import { GeometryEditor } from "../../../modules/superadmin/space-manager/components/geometry/GeometryEditor";
import { DeploySection } from "../../../modules/superadmin/space-manager/components/deploy/DeploySection";
import { ZoneEditor } from "../../../modules/superadmin/space-manager/components/zones/ZoneEditor";
import { useSpaceStore } from "../../../modules/superadmin/space-manager/stores/spaceStore";
import { Settings2, Sun, Move, ChevronDown, Group } from "lucide-react";
import { cn } from "../../../modules/superadmin/space-manager/lib/utils";
import "../../../modules/superadmin/space-manager/space-manager.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

type TabValue = "surfaces" | "zones" | "shading" | "geometry" | null;

function ToolsTabs() {
  const [activeTab, setActiveTab] = React.useState<TabValue>(null);
  // The old free-3D editor stays reachable while the new one proves itself
  const [legacyGeometry, setLegacyGeometry] = React.useState(false);

  const tabs = [
    { value: "surfaces" as const, label: "Surfaces", icon: Settings2 },
    { value: "zones" as const, label: "Zones", icon: Group },
    { value: "shading" as const, label: "Shading", icon: Sun },
    { value: "geometry" as const, label: "Geometry", icon: Move },
  ];

  const handleTabClick = (value: TabValue) => {
    setActiveTab(activeTab === value ? null : value);
  };

  return (
    <div className="space-y-2">
      <div className="inline-flex items-center rounded-lg bg-muted p-1 text-muted-foreground">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.value;
          return (
            <button
              key={tab.value}
              onClick={() => handleTabClick(tab.value)}
              className={cn(
                "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium transition-all",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                isActive
                  ? "bg-background text-foreground shadow"
                  : "hover:bg-background/50"
              )}
            >
              <Icon className="h-4 w-4" />
              {tab.label}
              <ChevronDown
                className={cn(
                  "h-3 w-3 transition-transform",
                  isActive && "rotate-180"
                )}
              />
            </button>
          );
        })}
      </div>

      <div style={{ display: activeTab === "surfaces" ? undefined : "none" }}>
        <SurfaceManagement />
      </div>
      <div style={{ display: activeTab === "zones" ? undefined : "none" }}>
        <ZoneEditor />
      </div>
      <div style={{ display: activeTab === "shading" ? undefined : "none" }}>
        <ShadingSection />
      </div>
      <div style={{ display: activeTab === "geometry" ? undefined : "none" }} className="space-y-2">
        {legacyGeometry ? <PlaneEditor3D /> : <GeometryEditor />}
        <button
          onClick={() => setLegacyGeometry((v) => !v)}
          className="text-xs text-muted-foreground underline"
        >
          {legacyGeometry ? "Back to the geometry editor" : "Open the legacy 3D editor"}
        </button>
      </div>
    </div>
  );
}

function SpaceManagerContent() {
  const { spaceId, spaceInfo } = useSpaceStore();
  const [selectedProduct, setSelectedProduct] = React.useState("");
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  return (
    <div className="space-manager-root min-h-screen bg-background">
      {/* Page header */}
      <div className="border-b bg-card sticky top-0 z-40">
        <div className="mx-auto px-4 h-14 flex items-center justify-between">
          <h1 className="text-xl font-bold text-foreground">Space Manager</h1>
          {spaceInfo && (
            <span className="text-sm text-muted-foreground">
              {spaceInfo.name} ({spaceInfo.width}x{spaceInfo.height})
            </span>
          )}
        </div>
      </div>

      {/* Main Content with sidebar */}
      <div className="flex">
        {/* Left: main content */}
        <main className="flex-1 min-w-0 px-4 py-6 space-y-6">
          <SpaceLoader />
          {spaceId && (
            <TextureMapping
              selectedProduct={selectedProduct}
              onProductSelect={setSelectedProduct}
              sidebarOpen={sidebarOpen}
              onToggleSidebar={() => setSidebarOpen((o) => !o)}
            />
          )}
          {spaceId && <ToolsTabs />}
          {spaceId && <DeploySection />}
        </main>

        {/* Right: product sidebar */}
        {spaceId && sidebarOpen && (
          <aside className="flex-shrink-0 border-l bg-card sticky top-14 h-[calc(100vh-3.5rem)] overflow-hidden" style={{ width: 420 }}>
            <ProductSidebar
              selectedProduct={selectedProduct}
              onProductSelect={setSelectedProduct}
            />
          </aside>
        )}
      </div>
    </div>
  );
}

export function SpaceManagerPage() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <SpaceManagerContent />
      </ToastProvider>
    </QueryClientProvider>
  );
}
